import React from 'react';
import styled from 'styled-components';
import type { StatusBarItem } from '../layout/StatusBar';
import { AppShell } from '../layout/AppShell';
import { DocumentInspectWorkspace, type DocumentInspectWorkspaceProps } from '../components/documentInspect/DocumentInspectWorkspace';
import { FirstSeenCard } from '../components/FirstSeenCard';

interface InspectScreenProps {
    caseTitle: string;
    statusItems: StatusBarItem[];
    documentTitle?: string;
    documentId?: string;
    onBackToOverview: () => void;
    onBackToCases?: () => void;
    workspaceProps?: DocumentInspectWorkspaceProps | null;
    error?: string | null;
    emptyMessage?: string;
    jobsIndicator?: React.ReactNode;
}

const ScreenContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    height: 100%;
`;

const ErrorText = styled.div`
    font-size: 13px;
    color: #999;
    padding: 24px 0;
`;

export const InspectScreen: React.FC<InspectScreenProps> = ({
    caseTitle,
    statusItems,
    documentTitle,
    documentId,
    onBackToOverview,
    onBackToCases,
    workspaceProps,
    error,
    emptyMessage = 'Select a document from the overview to inspect its pages.',
    jobsIndicator,
}) => {
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    // Breadcrumb renders inline in the compact document-header row (not the
    // AppShell header block). The trailing document-title item is dropped —
    // the editable title itself follows the breadcrumb in that row.
    const breadcrumb = [
        ...(onBackToCases ? [{ label: 'Cases', onClick: onBackToCases }] : []),
        { label: caseTitle, onClick: onBackToOverview },
    ];
    return (
        <AppShell
            statusItems={statusItems}
            jobsIndicator={jobsIndicator}
            constrainHeight
            compact
            onLogoClick={onBackToCases ?? onBackToOverview}
        >
            <ScreenContainer ref={containerRef}>
                {error && <ErrorText>{error}</ErrorText>}
                {!error && workspaceProps && (
                    <DocumentInspectWorkspace
                        {...workspaceProps}
                        header={{ ...workspaceProps.header, breadcrumb }}
                    />
                )}
                {!error && !workspaceProps && <ErrorText>{emptyMessage}</ErrorText>}
            </ScreenContainer>
            {workspaceProps && !error && (
                <FirstSeenCard
                    cardKey="document_view"
                    targetRef={containerRef}
                    title="You're inspecting a document"
                    placement="left"
                >
                    <p>This view is where you fix what the AI got wrong. You can:</p>
                    <ul>
                        <li><strong>Split</strong> a document if it should be two — pick a page and split after it.</li>
                        <li><strong>Merge</strong> with the previous or next document, from the toolbar&apos;s <strong>Actions</strong> menu.</li>
                        <li>Jump to the <strong>previous / next document</strong> with the toolbar arrows.</li>
                        <li><strong>Search</strong> within the document&apos;s OCR text.</li>
                        <li>Toggle the <strong>Image / Text / Side-by-Side</strong> tabs to view extracted OCR — drag the divider to resize the panes.</li>
                        <li><strong>Change</strong> or <strong>Confirm</strong> the category (its place on the intake checklist) — your choice survives re-analysis.</li>
                        <li>Mark it <strong>Reviewed</strong> when the boundaries are right. That&apos;s separate from confirming the category: one settles the split, the other the classification.</li>
                    </ul>
                </FirstSeenCard>
            )}
        </AppShell>
    );
};
